import { createPortal } from 'react-dom';
import type { Bilingual, Course } from '@/types/domain';
import { ORG } from '@/config/org';
import { formatDate } from '@/lib/format';

const fmt1 = (n: number) => (Number.isFinite(n) ? n.toFixed(1) : '—');

/**
 * Formal, print-only course-evaluation report for leadership (the dean).
 * Rendered into <body> and shown ONLY when printing — the live app (#root) is
 * hidden in print, so this fully replaces the page. Mirrors the certificate PDF
 * approach (window.print() + a portaled, print-only document). The webapp's own
 * analytics view is unchanged; this is a different, report-shaped rendering.
 */
export function SurveyReport({
  course,
  count,
  overallAvg,
  rows,
  comments,
}: {
  course: Course;
  count: number;
  overallAvg: number;
  rows: { label: Bilingual; avg: number }[];
  comments: string[];
}) {
  const today = formatDate(new Date(), 'th', { day: '2-digit', month: 'long', year: 'numeric' });

  return createPortal(
    <div className="survey-report" aria-hidden="true">
      <style>{`
        .survey-report { display: none; }
        /* Sarabun — Thailand's official government document font — for the whole report. */
        .survey-report, .survey-report .th { font-family: 'Sarabun','IBM Plex Sans Thai',sans-serif; }
        @media print {
          #root { display: none !important; }
          .survey-report {
            display: block !important;
            position: absolute; inset: 0; width: 100%;
            color: #1c1c1c; background: #fff;
          }
          @page { size: A4 portrait; margin: 18mm 16mm; }
          .sr-row { page-break-inside: avoid; }
          /* Comments start on a fresh page so they never wrap awkwardly off page 1. */
          .sr-comments { page-break-before: always; }
        }
      `}</style>

      {/* Letterhead */}
      <div style={{ textAlign: 'center', borderBottom: '2px solid #2f3b2a', paddingBottom: 14, marginBottom: 22 }}>
        <div className="th" style={{ fontSize: 20, fontWeight: 700 }}>{ORG.hospital.th}</div>
        <div style={{ fontSize: 12.5, color: '#555', marginTop: 2 }} className="th">
          {ORG.hospital.en} · {ORG.owner.th} ({ORG.owner.en})
        </div>
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div className="th" style={{ fontSize: 22, fontWeight: 700 }}>รายงานสรุปผลการประเมินหลักสูตร</div>
        <div style={{ fontSize: 11.5, color: '#777', marginTop: 4, letterSpacing: '.08em' }}>COURSE EVALUATION SUMMARY REPORT</div>
      </div>

      {/* Course meta */}
      <table style={{ width: '100%', fontSize: 13.5, marginBottom: 20, borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td className="th" style={{ padding: '4px 0', color: '#666', width: 150, verticalAlign: 'top' }}>หลักสูตร</td><td className="th" style={{ fontWeight: 600 }}>{course.title.th}</td></tr>
          <tr><td className="th" style={{ padding: '4px 0', color: '#666' }}>รหัส · วันที่สัมมนา</td><td>{course.code} · {course.seminar}</td></tr>
          <tr><td className="th" style={{ padding: '4px 0', color: '#666' }}>หน่วยงานที่จัด</td><td>{course.host}</td></tr>
          <tr><td className="th" style={{ padding: '4px 0', color: '#666' }}>จำนวนผู้ตอบแบบประเมิน</td><td className="th">{count} คน</td></tr>
          <tr><td className="th" style={{ padding: '4px 0', color: '#666' }}>วันที่ออกรายงาน</td><td className="th">{today}</td></tr>
        </tbody>
      </table>

      {/* Overall satisfaction */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px', background: '#f3f5ef', borderRadius: 8, marginBottom: 22 }}>
        <span className="th" style={{ fontSize: 14, color: '#444' }}>ความพึงพอใจโดยรวม</span>
        <span style={{ fontSize: 26, fontWeight: 800, color: '#2f3b2a' }}>{fmt1(overallAvg)}</span>
        <span style={{ fontSize: 13, color: '#777' }}>/ 5.0</span>
      </div>

      {/* Per-question averages */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, marginBottom: 24 }}>
        <thead>
          <tr style={{ borderBottom: '1.5px solid #2f3b2a', textAlign: 'left' }}>
            <th style={{ padding: '8px 6px', width: 32 }}>#</th>
            <th className="th" style={{ padding: '8px 6px' }}>หัวข้อการประเมิน</th>
            <th className="th" style={{ padding: '8px 6px', width: 130, textAlign: 'right' }}>ค่าเฉลี่ย (เต็ม 5)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="sr-row" style={{ borderBottom: '1px solid #e4e4e0' }}>
              <td style={{ padding: '8px 6px', color: '#999' }}>{i + 1}</td>
              <td className="th" style={{ padding: '8px 6px', lineHeight: 1.45 }}>{r.label.th}</td>
              <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700 }}>{fmt1(r.avg)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Comments */}
      {comments.length > 0 && (
        <div className="sr-comments" style={{ marginBottom: 26 }}>
          <div className="th" style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, borderBottom: '1px solid #ccc', paddingBottom: 5 }}>ความคิดเห็นและข้อเสนอแนะเพิ่มเติม</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {comments.map((c, i) => (
              <div key={i} className="th sr-row" style={{ fontSize: 13, lineHeight: 1.55, paddingLeft: 14, borderLeft: '2px solid #c9d3bf', color: '#333' }}>“{c}”</div>
            ))}
          </div>
        </div>
      )}

      {/* Footer / signature — generous gap so it never crowds the table/comments */}
      <div style={{ marginTop: 72, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: 12.5, color: '#555' }}>
        <div className="th" style={{ lineHeight: 1.5 }}>จัดทำโดยระบบ Warm Paper ED<br />{ORG.hospital.th}</div>
        <div className="th" style={{ textAlign: 'center', borderTop: '1px solid #999', width: 210, paddingTop: 6 }}>ลงชื่อผู้จัดทำ / Prepared by</div>
      </div>
    </div>,
    document.body,
  );
}
